import type { CSSProperties } from 'react';

import type { SupportedLanguage } from '@/i18n';

/**
 * The BMO brand image in a header, swapping to the short mark on small canvases.
 *
 * ## Why the short mark exists
 *
 * The full lockup is ~10:1 — at the 24px cap height the header uses, that is
 * 246px of horizontal space before the language menu, the identity block and
 * the avatar get any. On a 375px phone it either crowds them off the bar or
 * forces the bar to wrap. The short mark (the "BMO" wordmark plus the
 * medallion, no "Global Asset Management") is 2.9:1, so it costs 70px at the
 * same cap height and the medallion renders at exactly the same size.
 *
 * ## One ceiling, both ranges
 *
 * Phone is 375–767px and tablet is 768–1023px, so a single `max-width: 1023px`
 * covers both — and everything below 375px too, where the wide lockup would be
 * even less wearable. Written out as one query rather than two adjacent ones
 * because there is no width in that span that wants different art.
 *
 * ## `<picture>`, not two `<img>`s toggled by CSS
 *
 * Rendering both and hiding one with `display: none` downloads both: a hidden
 * image is still fetched in every engine. `<picture>` makes the browser pick
 * ONE source before it requests anything, so a phone never pays for the lockup
 * it will not draw. It is also still a pure layout rule — no `window.innerWidth`
 * read, so it survives rotation, split-view and a zoomed desktop, which is the
 * same reasoning the rest of this header's responsive behaviour follows.
 *
 * The `alt` lives on the `<img>` and describes the SITE, not whichever artwork
 * the viewport happened to select — a screen reader user resizing their window
 * must not hear the page rename itself.
 */

/** Phone (375–767px) and tablet (768–1023px). See the note above. */
export const COMPACT_BRAND_MEDIA = '(max-width: 1023px)';

/** `colour` for the white header bars, `reverse` for the blue one. */
export type BrandMarkTone = 'colour' | 'reverse';

type Art = {
  src: string;
  /** INTRINSIC dimensions, not display size. They exist so the browser can
   *  reserve the right aspect ratio before the SVG lands; CSS sets the actual
   *  height and leaves width auto, so these never size anything themselves. */
  width: number;
  height: number;
};

const MARK: Record<BrandMarkTone, Art> = {
  colour: { src: '/bmo-mark.svg', width: 209, height: 72 },
  reverse: { src: '/bmo-mark-reverse.svg', width: 209, height: 72 },
};

/** The reverse lockup is the one asset here that is not per-language. That is
 *  pre-existing — AppShell's blue bar has always drawn the English artwork —
 *  and is left alone rather than quietly changed alongside a layout fix. */
const lockupFor = (tone: BrandMarkTone, language: SupportedLanguage): Art =>
  tone === 'colour'
    ? { src: `/bmo-gam-lockup-${language}.svg`, width: 782, height: 77 }
    : { src: '/bmo-global-asset-management.svg', width: 277, height: 27 };

export type BrandMarkProps = {
  tone: BrandMarkTone;
  language: SupportedLanguage;
  /** Empty when an ancestor button already carries the accessible name — two
   *  names on one control is a double announcement, not a clearer one. */
  alt: string;
  className?: string;
  /** Carries `--bmo-brand-h` where a header wants a cap height other than the
   *  24px default. Sizing both sources from one custom property is the point:
   *  an inline `height` would land on the `<img>` only and leave whichever
   *  source the viewport actually picked to be sized by something else. */
  style?: CSSProperties;
};

export function BrandMark({ tone, language, alt, className, style }: BrandMarkProps) {
  const lockup = lockupFor(tone, language);
  const mark = MARK[tone];

  return (
    <picture className={`bmo-brand${className ? ` ${className}` : ''}`} style={style}>
      <source
        media={COMPACT_BRAND_MEDIA}
        srcSet={mark.src}
        width={mark.width}
        height={mark.height}
      />
      <img src={lockup.src} alt={alt} width={lockup.width} height={lockup.height} />
    </picture>
  );
}
